import { useState } from 'react'
import { Alert, Badge, Button, Card, Container, Spinner, Table } from 'react-bootstrap'
import { runSmokeTests, type SmokeTestResponse } from '../services/api'

export default function TestRunner() {
  const [result, setResult] = useState<SmokeTestResponse | null>(null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')

  const handleRun = async () => {
    setRunning(true)
    setError('')
    try {
      setResult(await runSmokeTests())
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Smoke tests failed to run.'
      setError(message)
    } finally {
      setRunning(false)
    }
  }

  return (
    <Container fluid>
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="mb-1">Test Runner</h1>
          <p className="text-muted mb-0">Trigger production smoke tests for database, blob storage, Firecrawl, and Azure OpenAI.</p>
        </div>
        <Button onClick={handleRun} disabled={running}>
          {running && <Spinner animation="border" size="sm" className="me-2" />}
          {running ? 'Running tests...' : 'Run smoke tests'}
        </Button>
      </div>

      <Alert variant="warning">
        These checks make live calls to external services. Firecrawl and Azure OpenAI calls may consume a small amount of quota.
      </Alert>

      {error && <Alert variant="danger">{error}</Alert>}

      {!result && !running && (
        <Card body>
          <div className="text-muted">No smoke test has been triggered from this page yet.</div>
        </Card>
      )}

      {result && (
        <Card>
          <Card.Header className="d-flex align-items-center justify-content-between">
            <span>
              Result:{' '}
              <Badge bg={result.status === 'Passed' ? 'success' : 'danger'}>{result.status}</Badge>
            </span>
            <span className="small text-muted">Triggered {new Date(result.triggeredUtc).toLocaleString()}</span>
          </Card.Header>
          <Card.Body>
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Output</th>
                </tr>
              </thead>
              <tbody>
                {result.checks.map((check) => (
                  <tr key={check.name}>
                    <td className="fw-semibold">{check.name}</td>
                    <td>
                      <Badge bg={check.status === 'Healthy' ? 'success' : 'danger'}>{check.status}</Badge>
                    </td>
                    <td>{check.durationMs} ms</td>
                    <td>
                      <div>{check.detail || check.error || '-'}</div>
                      {check.error && <div className="small text-danger">{check.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  )
}
